import { useMemo, useState } from "react"

import { useAiHubContext } from "@contexts/main/ai_hub_context"
import {
  listAiMessageStepsQuery,
  listAiMessagesQuery
} from "@domain/ai_answers"
import { listAiEvalMocks } from "@domain/ai_eval_mocks"
import {
  AskQuestionEvalCaseResult,
  AskQuestionEvalCaseStatus,
  AskQuestionEvalRun,
  deleteAllAskQuestionEvalRuns,
  listAskQuestionEvalRuns,
  runAskQuestionEvalSuite
} from "@domain/ai_evals"
import { useCustomersQuery } from "@hooks/use_customers"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import PlayArrowIcon from "@mui/icons-material/PlayArrow"
import RefreshIcon from "@mui/icons-material/Refresh"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from "@mui/material"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { showErrorToast, showSuccessToast } from "@utils/toast"

import { CaseComparisonDrawer } from "./sub_components/CaseComparisonDrawer"

type LocalRunStatus = AskQuestionEvalCaseStatus | "RUNNING"
type LocalRun = Omit<AskQuestionEvalRun, "status"> & {
  status: LocalRunStatus
  _pending?: boolean
}

const isPending = (run: LocalRun) =>
  run._pending === true || run.status === "RUNNING"

const PIPELINE_OPTIONS = [
  { value: "FULL_FLOW", label: "Tam Akış" },
  { value: "RAG_PIPELINE", label: "Sadece RAG" },
  { value: "ACTION_PIPELINE", label: "Sadece Aksiyon" },
  { value: "INTENT_PIPELINE", label: "Sadece Intent" }
]

const MSGTYPE_OPTIONS = [
  { value: "TEXT_MESSAGE", label: "Chat (Text)" },
  { value: "COMMENT", label: "Comment" },
  { value: "QUESTION", label: "Question" },
  { value: "PRIVATE_REPLY", label: "Private Reply" }
]

const PIPELINE_SUPPORTS_MSGTYPE = [
  "FULL_FLOW",
  "RAG_PIPELINE",
  "ACTION_PIPELINE",
  "INTENT_PIPELINE"
]

export default function AiEvalsPage() {
  const MAX_RUNS = 50
  const queryClient = useQueryClient()
  const { customerId, setCustomerId } = useAiHubContext()
  const [selectedMockIds, setSelectedMockIds] = useState<string[]>([])
  const [selectedTemplate, setSelectedTemplate] = useState("FULL_FLOW")
  const [mockMsgType, setMockMsgType] = useState("TEXT_MESSAGE")
  const [selectedCase, setSelectedCase] =
    useState<AskQuestionEvalCaseResult | null>(null)
  const [liveQuery, setLiveQuery] = useState("")
  const [selectedStep, setSelectedStep] = useState("")
  const [actionMsgType, setActionMsgType] = useState("TEXT_MESSAGE")
  const [pendingStepRun, setPendingStepRun] =
    useState<AskQuestionEvalRun | null>(null)
  const [stepTestRunning, setStepTestRunning] = useState(false)
  const [sessionRuns, setSessionRuns] = useState<LocalRun[]>([])

  const { customerOptions, isLoading: customersLoading } = useCustomersQuery(
    true,
    true
  )

  const dbMocksQuery = useQuery({
    queryKey: ["ai-eval-mocks-picker"],
    queryFn: () => listAiEvalMocks({ limit: 200 })
  })

  const runsQuery = useQuery({
    queryKey: ["ask-question-eval-runs", customerId],
    queryFn: () =>
      listAskQuestionEvalRuns({
        customerId: customerId || undefined,
        offset: 0,
        limit: 20
      })
  })

  const apiRuns: LocalRun[] = (runsQuery.data?.data?.docs || []) as LocalRun[]
  const runs: LocalRun[] = [...sessionRuns, ...apiRuns]
    .filter(
      (run, index, arr) =>
        arr.findIndex((item) => item._id === run._id) === index
    )
    .slice(0, MAX_RUNS)
  const dbMocks = dbMocksQuery.data?.data?.docs || []
  const selectedMocks = dbMocks.filter((m) => selectedMockIds.includes(m._id))
  const selectedCaseCount = selectedMocks.reduce(
    (sum, m) => sum + (m.cases?.length || 0),
    0
  )
  const canRun = selectedMockIds.length > 0 && Boolean(customerId)
  const hasPendingRuns = sessionRuns.some(isPending)

  function startMockRuns() {
    if (!canRun) return
    const targetMockIds = [...selectedMockIds]
    const baseTime = Date.now()

    for (let i = 0; i < targetMockIds.length; i++) {
      const mockId = targetMockIds[i]
      const mock = dbMocks.find((m) => m._id === mockId)
      const pendingId = `pending-${baseTime}-${i}-${mockId}`
      const placeholder: LocalRun = {
        _id: pendingId,
        _pending: true,
        suiteId: "db-mock",
        suiteTitle: mock?.title ?? "Mock Eval",
        mode: "MOCK",
        customerId,
        status: "RUNNING",
        total: 1,
        passed: 0,
        failed: 0,
        durationMs: 0,
        cases: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }
      setSessionRuns((prev) => [placeholder, ...prev].slice(0, MAX_RUNS))

      runAskQuestionEvalSuite({
        suiteId: "db-mock",
        mode: "MOCK",
        customerId: customerId || undefined,
        mockId,
        template: selectedTemplate,
        msgType: PIPELINE_SUPPORTS_MSGTYPE.includes(selectedTemplate)
          ? mockMsgType
          : undefined
      })
        .then((response) => {
          setSessionRuns((prev) => {
            const withoutPlaceholder = prev.filter((r) => r._id !== pendingId)
            if (response.isSuccess && response.data) {
              return [response.data as LocalRun, ...withoutPlaceholder].slice(
                0,
                MAX_RUNS
              )
            }
            return withoutPlaceholder
          })
          if (response.isSuccess && response.data) {
            showSuccessToast(
              `${mock?.title || "Eval"}: ${response.data.passed}/${response.data.total}`
            )
          } else {
            showErrorToast(response.errorMessage || "Eval run failed")
          }
          queryClient.invalidateQueries({
            queryKey: ["ask-question-eval-runs"]
          })
        })
        .catch((err: unknown) => {
          setSessionRuns((prev) => prev.filter((r) => r._id !== pendingId))
          showErrorToast(err instanceof Error ? err.message : "Eval run failed")
        })
    }
    setSelectedMockIds([])
  }

  const selectedCaseStepsQuery = useQuery({
    queryKey: [
      "ai-message-steps",
      selectedCase?.aiMessageId,
      (selectedCase?.actual as { conversationId?: string } | undefined)
        ?.conversationId,
      customerId
    ],
    enabled: Boolean(
      selectedCase?.aiMessageId ||
      (selectedCase?.actual as { conversationId?: string } | undefined)
        ?.conversationId
    ),
    queryFn: async () => {
      const fromActual = selectedCase?.actual as
        | { conversationId?: string }
        | undefined
      const conversationId = fromActual?.conversationId
      let aiMessageId = selectedCase?.aiMessageId

      if (!aiMessageId && conversationId && customerId) {
        const msgResponse = await listAiMessagesQuery({
          customerId,
          conversationId,
          limit: 1,
          offset: 0
        })
        aiMessageId = msgResponse.data?.docs?.[0]?._id || undefined
      }

      if (!aiMessageId) return []
      const stepResponse = await listAiMessageStepsQuery(aiMessageId)
      return stepResponse.isSuccess && stepResponse.data
        ? stepResponse.data
        : []
    }
  })

  const selectedCaseActual = useMemo(() => {
    const baseActual = selectedCase?.actual || {}
    const fetchedSteps = selectedCaseStepsQuery.data || []
    if (!fetchedSteps.length) return baseActual
    return {
      ...baseActual,
      steps: fetchedSteps.map((s) => s.step),
      stepDetails: fetchedSteps.map((s) => ({
        step: s.step,
        status: s.status,
        durationMs: s.durationMs,
        source: s.source,
        request: s.request,
        response: s.response
      }))
    }
  }, [selectedCase?.actual, selectedCaseStepsQuery.data])

  function startLiveStepTest() {
    if (!customerId || !liveQuery.trim()) return
    setStepTestRunning(true)
    setPendingStepRun(null)
    runAskQuestionEvalSuite({
      suiteId: "ask-question-step-live",
      mode: "LIVE",
      customerId,
      scenarios: [
        {
          id: `step-${Date.now()}`,
          title: selectedStep
            ? `Step Test: ${selectedStep}`
            : "Normal Flow Test",
          query: liveQuery.trim(),
          customerId,
          ...(selectedStep
            ? { stepTest: { step: selectedStep, msgType: actionMsgType } }
            : {})
        }
      ]
    })
      .then((response) => {
        setStepTestRunning(false)
        if (!response.isSuccess) {
          showErrorToast(response.errorMessage || "Step test failed")
          return
        }
        if (!response.data) return
        setPendingStepRun(response.data)
        showSuccessToast("Step test completed. Oldu / Olmadı ile onayla.")
      })
      .catch((err: unknown) => {
        setStepTestRunning(false)
        showErrorToast(err instanceof Error ? err.message : "Step test failed")
      })
  }

  const saveStepReview = (status: "PASSED" | "FAILED") => {
    if (!pendingStepRun) return
    const reviewedRun: LocalRun = {
      ...pendingStepRun,
      status,
      passed: status === "PASSED" ? pendingStepRun.total : 0,
      failed: status === "FAILED" ? pendingStepRun.total : 0,
      cases: pendingStepRun.cases.map((item) => ({
        ...item,
        status,
        failures:
          status === "FAILED"
            ? ["Kullanıcı manuel olarak başarısız işaretledi"]
            : []
      }))
    }
    setSessionRuns((prev) => [reviewedRun, ...prev].slice(0, MAX_RUNS))
    setPendingStepRun(null)
    queryClient.invalidateQueries({ queryKey: ["ask-question-eval-runs"] })
  }

  const stepOptions = [
    "NORMAL_FLOW",
    "INTENT_PIPELINE",
    "ACTION_PIPELINE",
    "RAG_PIPELINE",
    "FINALIZE_AI_CONTENT"
  ]

  const showPipelineContext = [
    "INTENT_PIPELINE",
    "ACTION_PIPELINE",
    "RAG_PIPELINE",
    "FINALIZE_AI_CONTENT"
  ].includes(selectedStep)

  return (
    <Box sx={{ display: "flex", height: "100%", overflow: "hidden" }}>
      <style>{`@keyframes spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }`}</style>

      {/* ── Sol Panel: Konfigürasyon ── */}
      <Box
        sx={{
          width: 320,
          minWidth: 320,
          borderRight: 1,
          borderColor: "divider",
          overflow: "auto",
          display: "flex",
          flexDirection: "column"
        }}
      >
        {/* Mock Eval Bölümü */}
        <Box sx={{ p: 2 }}>
          <Typography
            variant="subtitle2"
            fontWeight={700}
            color="text.secondary"
            sx={{
              mb: 2,
              textTransform: "uppercase",
              letterSpacing: 0.5,
              fontSize: 11
            }}
          >
            Mock Eval
          </Typography>
          <Stack spacing={1.5}>
            <Autocomplete
              multiple
              size="small"
              options={dbMocks}
              value={selectedMocks}
              onChange={(_, value) =>
                setSelectedMockIds(value.map((v) => v._id))
              }
              getOptionLabel={(option) =>
                `${option.title} (${option.cases?.length || 0} case)`
              }
              isOptionEqualToValue={(option, value) => option._id === value._id}
              loading={dbMocksQuery.isLoading}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option._id}
                    label={`${option.title} (${option.cases?.length || 0})`}
                    size="small"
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={
                    selectedMockIds.length > 0
                      ? `Test Senaryoları (${selectedCaseCount} case)`
                      : "Test Senaryoları"
                  }
                  placeholder={
                    selectedMockIds.length === 0
                      ? "Ara — birden fazla seçilebilir"
                      : ""
                  }
                />
              )}
            />
            <Autocomplete
              size="small"
              options={customerOptions}
              value={
                customerOptions.find((item) => item.id === customerId) || null
              }
              onChange={(_, value) => setCustomerId(value?.id || "")}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              loading={customersLoading}
              disabled={customersLoading}
              renderInput={(params) => (
                <TextField {...params} label="Müşteri" placeholder="Ara..." />
              )}
            />
            <TextField
              select
              size="small"
              label="Pipeline"
              value={selectedTemplate}
              onChange={(e) => setSelectedTemplate(e.target.value)}
              fullWidth
            >
              {PIPELINE_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>
            {PIPELINE_SUPPORTS_MSGTYPE.includes(selectedTemplate) && (
              <TextField
                select
                size="small"
                label="Mesaj Tipi"
                value={mockMsgType}
                onChange={(e) => setMockMsgType(e.target.value)}
                fullWidth
              >
                {MSGTYPE_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
            {!customerId && (
              <Alert severity="warning" sx={{ py: 0.5, fontSize: 12 }}>
                Önce müşteri seçmelisin
              </Alert>
            )}
            <Tooltip
              title={
                !canRun
                  ? "En az 1 senaryo ve müşteri seçmen gerekiyor"
                  : "Seçilen senaryolar paralel başlatılır — UI bloke olmaz"
              }
            >
              <span>
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<PlayArrowIcon />}
                  disabled={!canRun}
                  onClick={startMockRuns}
                >
                  {selectedMockIds.length > 1
                    ? `Testleri Çalıştır (${selectedMockIds.length} senaryo, ${selectedCaseCount} case)`
                    : selectedMockIds.length === 1
                      ? `Testleri Çalıştır (${selectedCaseCount} case)`
                      : "Testleri Çalıştır"}
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </Box>

        <Divider />

        {/* Canlı Step Testi Bölümü */}
        <Box sx={{ p: 2 }}>
          <Typography
            variant="subtitle2"
            fontWeight={700}
            color="text.secondary"
            sx={{
              mb: 2,
              textTransform: "uppercase",
              letterSpacing: 0.5,
              fontSize: 11
            }}
          >
            Canlı Step Testi
          </Typography>
          <Stack spacing={1.5}>
            <FormControl size="small" fullWidth>
              <InputLabel>Step</InputLabel>
              <Select
                label="Step"
                value={selectedStep || "NORMAL_FLOW"}
                onChange={(e) =>
                  setSelectedStep(
                    e.target.value === "NORMAL_FLOW" ? "" : e.target.value
                  )
                }
              >
                {stepOptions.map((step) => (
                  <MenuItem key={step} value={step}>
                    {step}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {showPipelineContext && (
              <FormControl size="small" fullWidth>
                <InputLabel>Pipeline Context</InputLabel>
                <Select
                  label="Pipeline Context"
                  value={actionMsgType}
                  onChange={(e) => setActionMsgType(e.target.value)}
                >
                  <MenuItem value="TEXT_MESSAGE">chat</MenuItem>
                  <MenuItem value="COMMENT">comment</MenuItem>
                  <MenuItem value="QUESTION">question</MenuItem>
                  <MenuItem value="PRIVATE_REPLY">private reply</MenuItem>
                </Select>
              </FormControl>
            )}

            <TextField
              label="Query"
              size="small"
              value={liveQuery}
              onChange={(e) => setLiveQuery(e.target.value)}
              multiline
              minRows={3}
            />

            <Button
              variant="outlined"
              fullWidth
              disabled={!customerId || !liveQuery.trim() || stepTestRunning}
              onClick={startLiveStepTest}
              startIcon={
                stepTestRunning ? <CircularProgress size={14} /> : undefined
              }
            >
              {stepTestRunning ? "Çalışıyor..." : "Run Step Test"}
            </Button>

            {pendingStepRun && (
              <Paper
                variant="outlined"
                sx={{ p: 1.5, bgcolor: "background.default" }}
              >
                <Typography variant="caption" color="text.secondary">
                  Test Cevabı
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ whiteSpace: "pre-wrap", mt: 0.5, mb: 1 }}
                >
                  {pendingStepRun.cases?.[0]?.answer || "-"}
                </Typography>
                <Button
                  size="small"
                  sx={{ mb: 1 }}
                  onClick={() =>
                    pendingStepRun.cases?.[0] &&
                    setSelectedCase(pendingStepRun.cases[0])
                  }
                >
                  Step Detayını Aç
                </Button>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="contained"
                    color="success"
                    size="small"
                    fullWidth
                    onClick={() => saveStepReview("PASSED")}
                  >
                    ✓ Oldu
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    size="small"
                    fullWidth
                    onClick={() => saveStepReview("FAILED")}
                  >
                    ✗ Olmadı
                  </Button>
                </Stack>
              </Paper>
            )}
          </Stack>
        </Box>
      </Box>

      {/* ── Sağ Panel: Sonuçlar ── */}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          display: "flex",
          flexDirection: "column"
        }}
      >
        {/* List header — daima görünür */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            px: 2,
            py: 1,
            borderBottom: 1,
            borderColor: "divider",
            bgcolor: "background.paper",
            position: "sticky",
            top: 0,
            zIndex: 1
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="body2" fontWeight={600}>
              Test Çalıştırmaları
            </Typography>
            {runs.length > 0 && (
              <Typography variant="caption" color="text.secondary">
                · {runs.length}
                {hasPendingRuns && (
                  <>
                    {" · "}
                    <Box
                      component="span"
                      sx={{ color: "info.main", fontWeight: 600 }}
                    >
                      {sessionRuns.filter(isPending).length} devam ediyor
                    </Box>
                  </>
                )}
              </Typography>
            )}
          </Stack>
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="DB'den yenile">
              <span>
                <IconButton
                  size="small"
                  onClick={() => runsQuery.refetch()}
                  disabled={runsQuery.isRefetching}
                >
                  <RefreshIcon
                    fontSize="small"
                    sx={{
                      animation: runsQuery.isRefetching
                        ? "spin 1s linear infinite"
                        : "none"
                    }}
                  />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Tüm test sonuçlarını sil">
              <span>
                <IconButton
                  size="small"
                  sx={{
                    color: "text.disabled",
                    ":hover": { color: "error.main" }
                  }}
                  onClick={async () => {
                    if (
                      !window.confirm(
                        "Tüm test sonuçları silinsin mi? Bu işlem geri alınamaz."
                      )
                    )
                      return
                    const res = await deleteAllAskQuestionEvalRuns(
                      customerId || undefined
                    )
                    if (res.isSuccess) {
                      setSessionRuns([])
                      queryClient.invalidateQueries({
                        queryKey: ["ask-question-eval-runs"]
                      })
                    }
                  }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>

        {/* İçerik */}
        <Box sx={{ flex: 1, p: 2, overflow: "auto" }}>
          {runs.length === 0 ? (
            <Stack
              alignItems="center"
              justifyContent="center"
              sx={{ height: "100%", px: 3 }}
              spacing={2}
            >
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  bgcolor: "action.hover",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                <PlayArrowIcon sx={{ fontSize: 32, color: "text.disabled" }} />
              </Box>
              <Stack alignItems="center" spacing={0.5}>
                <Typography variant="body1" fontWeight={600}>
                  Henüz çalıştırılmış test yok
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  align="center"
                  sx={{ maxWidth: 280 }}
                >
                  Sol panelden senaryo ve müşteri seç, &quot;Testleri
                  Çalıştır&quot; ile başlat. Sonuçlar burada görünecek.
                </Typography>
              </Stack>
            </Stack>
          ) : (
            <Stack spacing={1}>
              {runs.map((run) => (
                <RunCard
                  key={run._id}
                  run={run}
                  onSelectCase={setSelectedCase}
                />
              ))}
            </Stack>
          )}
        </Box>
      </Box>

      {/* ── Case karşılaştırma paneli ── */}
      <CaseComparisonDrawer
        selectedCase={selectedCase}
        actual={selectedCaseActual}
        stepsLoading={selectedCaseStepsQuery.isLoading}
        onClose={() => setSelectedCase(null)}
      />
    </Box>
  )
}

function RunCard({
  run,
  onSelectCase
}: {
  run: LocalRun
  onSelectCase: (c: AskQuestionEvalCaseResult) => void
}) {
  const pending = isPending(run)
  const passRate =
    run.total > 0 ? Math.round((run.passed / run.total) * 100) : 0
  const allPassed = run.passed === run.total && run.total > 0

  return (
    <Accordion
      disableGutters
      variant="outlined"
      disabled={pending}
      sx={{
        "&:before": { display: "none" },
        borderRadius: "8px !important",
        bgcolor: pending ? "action.hover" : "background.paper",
        "&.Mui-disabled": { bgcolor: "action.hover", opacity: 1 }
      }}
    >
      <AccordionSummary
        expandIcon={pending ? null : <ExpandMoreIcon />}
        sx={{ minHeight: 52, "& .MuiAccordionSummary-content": { my: 1 } }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          sx={{ flex: 1, pr: 1 }}
        >
          {pending ? (
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.75}
              sx={{
                px: 1,
                py: 0.25,
                borderRadius: 1,
                bgcolor: "info.main",
                color: "#fff"
              }}
            >
              <CircularProgress size={11} sx={{ color: "#fff" }} />
              <Typography variant="caption" fontWeight={600}>
                Çalışıyor
              </Typography>
            </Stack>
          ) : (
            <StatusChip status={run.status as AskQuestionEvalCaseStatus} />
          )}
          <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }} noWrap>
            {run.suiteTitle || run.mode}
          </Typography>
          {pending ? (
            <Typography variant="caption" color="text.disabled">
              —/—
            </Typography>
          ) : (
            <Chip
              size="small"
              label={`${run.passed}/${run.total} (${passRate}%)`}
              color={
                allPassed ? "success" : run.passed > 0 ? "warning" : "error"
              }
              variant="outlined"
              sx={{ fontWeight: 600 }}
            />
          )}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ minWidth: 80, textAlign: "right" }}
          >
            {new Date(run.createdAt).toLocaleString("tr-TR", {
              month: "short",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit"
            })}
          </Typography>
        </Stack>
      </AccordionSummary>
      {!pending && (
        <AccordionDetails sx={{ p: 0 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "action.hover" }}>
                <TableCell sx={{ width: 90 }}>Durum</TableCell>
                <TableCell sx={{ width: "28%" }}>Test</TableCell>
                <TableCell>AI cevabı</TableCell>
                <TableCell sx={{ width: 200 }}>Sorun</TableCell>
                <TableCell sx={{ width: 60 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {run.cases.map((c) => (
                <TableRow key={c.scenarioId} hover>
                  <TableCell>
                    <StatusChip status={c.status} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">{c.title}</Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={c.answer || ""} placement="top-start">
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: "block",
                          maxWidth: 420,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap"
                        }}
                      >
                        {c.answer || "-"}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <FailureTags failures={c.failures || []} />
                  </TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={() => onSelectCase(c)}>
                      Detay
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </AccordionDetails>
      )}
    </Accordion>
  )
}

/** Compresses engine failure strings into scannable tags so the table shows *what* broke. */
const FAILURE_TAG_RULES: { match: RegExp; label: string }[] = [
  { match: /^Expected status/, label: "durum" },
  { match: /^Expected aiAction/, label: "aksiyon" },
  {
    match: /^Expected the AI to answer, but it escalated/,
    label: "eskalasyon"
  },
  { match: /^Expected the AI to answer, but no content/, label: "boş cevap" },
  { match: /^Expected content to include/, label: "içerik" },
  { match: /^Expected step/, label: "adım eksik" },
  { match: /^Forbidden step/, label: "yasak adım" }
]

function FailureTags({ failures }: { failures: string[] }) {
  if (failures.length === 0) {
    return (
      <Typography variant="caption" color="text.disabled">
        —
      </Typography>
    )
  }

  const labels = new Set<string>()
  let other = 0
  failures.forEach((failure) => {
    // Multi-turn failures arrive prefixed with "Turn N: "
    const bare = failure.replace(/^Turn \d+:\s*/, "")
    const rule = FAILURE_TAG_RULES.find((r) => r.match.test(bare))
    if (rule) labels.add(rule.label)
    else other++
  })
  if (other > 0) labels.add("diğer")

  return (
    <Tooltip
      placement="top-start"
      title={<Box sx={{ whiteSpace: "pre-line" }}>{failures.join("\n")}</Box>}
    >
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
        {Array.from(labels).map((label) => (
          <Chip
            key={label}
            size="small"
            label={label}
            color="error"
            variant="outlined"
            sx={{ fontSize: 10, height: 20 }}
          />
        ))}
      </Stack>
    </Tooltip>
  )
}

function StatusChip({ status }: { status: AskQuestionEvalCaseStatus }) {
  return (
    <Chip
      size="small"
      label={status === "PASSED" ? "Geçti" : "Başarısız"}
      color={status === "PASSED" ? "success" : "error"}
    />
  )
}
